import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';

const PartnerColumn = ({ title, description, buttonText, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  const columnVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: 0.15 * index,
        ease: "easeOut"
      }
    }
  };
  
  const titleVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        delay: 0.2 + (0.15 * index),
        ease: "easeOut"
      }
    }
  };
  
  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.3 + (0.15 * index),
        duration: 0.4,
        type: "spring",
        stiffness: 200
      }
    },
    hover: {
      scale: 1.05,
      backgroundColor: "rgba(0, 143, 122, 0.9)",
      color: "white",
      transition: {
        duration: 0.2,
        type: "spring",
        stiffness: 400
      }
    },
    tap: { scale: 0.95 }
  };
  
  return (
    <motion.div
      className="partner-column"
      ref={ref}
      variants={columnVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <motion.h3 
        className="column-title"
        variants={titleVariants}
      >
        {title}
      </motion.h3>
      <p className="column-description">
        {description}
      </p>
      <motion.button
        className="partner-button"
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
      >
        {buttonText}
      </motion.button>
    </motion.div>
  );
};

const Partners = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  
  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  
  const bannerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: 0.3,
        ease: "easeOut"
      }
    }
  };
  
  const bannerTextVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: 0.6,
        ease: "easeOut"
      }
    }
  };
  
  const partnerData = [
    {
      title: "Consumer",
      description: "Nigeria's sole hyper-growth start-up is powered by brilliant minds, executing with mastery.",
      buttonText: "Join as an employee"
    },
    {
      title: "Drive partners",
      description: "Over 3,000 drive partners thrive on our platform thanks to a wide range of features that guarantee continuous growth.",
      buttonText: "Join as a drive partner"
    },
    {
      title: "Merchants",
      description: "Our platform delivers cutting-edge technologies to more than 1,500 super merchants, enabling exponential growth.",
      buttonText: "Join as a merchant"
    },
    {
      title: "Enterprise",
      description: "Tailored solutions for businesses of all sizes, powering operations with reliable and scalable technology.",
      buttonText: "Join as an enterprise"
    }
  ];

  return (
    <>
      <section className="partners-section" ref={ref}>
        <div className="partners-container">
          <motion.h2 
            className="partners-title"
            variants={titleVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            Hop on board!
          </motion.h2>
          
          <div className="partners-columns">
            {partnerData.map((partner, index) => (
              <PartnerColumn
                key={partner.title}
                title={partner.title}
                description={partner.description}
                buttonText={partner.buttonText}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      <motion.section 
        className="banner-section"
        variants={bannerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.6 }}
      >
        <div className="banner-container">
          <motion.h2 
            className="banner-title"
            variants={bannerTextVariants}
          >
            We build products that nations run on
          </motion.h2>
        </div>
      </motion.section>
    </>
  );
};

export default Partners;